import type { SelectCreatableProps } from '@ztechapps/webui.input'
import { SelectCreatable } from '@ztechapps/webui.input'
import { useSpreadsheetCellSelectAdapter } from './useSpreadsheetCellSelectAdapter'

/**
 * Adapter wrapping `<SelectCreatable>` for spreadsheet cells. Shared logic lives in
 * `useSpreadsheetCellSelectAdapter`.
 *
 * Tab inside an open Creatable menu uses the global `formatCreateLabel: 'Press TAB to create "<value>"'`
 * (`useReactSelect.ts:19-23`); react-select's default `tabSelectsValue=true` fires `onChange`
 * once and the cell's `onCommit` exits edit mode. The container handler stays out of edit-mode
 * arrow/Enter dispatch.
 *
 * Typed text left by a click, a typed-edit arrow or Shift+Tab commits as the matching option's
 * label, or as a new option when the text matches no label.
 */
export type SpreadsheetCellSelectCreatableProps = SelectCreatableProps & {
  onCommit?: () => void
  onMenuStateChange?: (isOpen: boolean) => void
}

export const SpreadsheetCellSelectCreatable = ({
  onCommit,
  onMenuStateChange,
  onChange,
  onInputChange,
  onMenuOpen,
  onMenuClose,
  ...rest
}: SpreadsheetCellSelectCreatableProps) => {
  const { wrapperProps, selectProps } = useSpreadsheetCellSelectAdapter({
    onCommit,
    onMenuStateChange,
    onChange,
    onInputChange,
    onMenuOpen,
    onMenuClose,
    defaultMenuIsOpen: rest.defaultMenuIsOpen,
    defaultInputValue: rest.defaultInputValue,
    options: rest.options,
    isCreatable: true,
  })

  return (
    <div {...wrapperProps}>
      <SelectCreatable {...rest} {...selectProps} />
    </div>
  )
}
